/**
 * Driver app hamburger menu — fully data-driven side drawer.
 * Add a new feature by appending an object to the `items` array
 * passed from the dashboard (section, key, label, sub, icon, to | onClick).
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, ChevronRight, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";

export const DriverMenu = ({ items, driverName, roleLabel }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const sections = items.reduce((acc, item) => {
    (acc[item.section] = acc[item.section] || []).push(item);
    return acc;
  }, {});

  const activate = (item) => {
    setOpen(false);
    if (item.to) navigate(item.to);
    else item.onClick?.();
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="driver-menu-btn"
          aria-label="Izbornik"
          className="p-3 rounded-full hover:bg-white/5 text-zinc-300 hover:text-[#C5A059] transition-colors"
        >
          <Menu className="w-6 h-6" />
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-[85vw] max-w-sm bg-[#0a0a0a] border-l border-white/10 p-0 [&>button]:hidden"
        data-testid="driver-menu-drawer"
      >
        <div className="flex items-center justify-between px-5 h-16 border-b border-white/10">
          <div>
            <SheetTitle className="text-sm text-white font-medium text-left">{driverName || "…"}</SheetTitle>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">{roleLabel}</p>
          </div>
          <SheetClose asChild>
            <button data-testid="driver-menu-close" aria-label="Zatvori"
                    className="p-2 rounded-full hover:bg-white/5 text-zinc-400 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </SheetClose>
        </div>
        <div className="overflow-y-auto h-[calc(100%-4rem)] px-3 py-4 space-y-5">
          {Object.entries(sections).map(([section, rows]) => (
            <div key={section}>
              <p className="px-2 mb-2 text-[10px] uppercase tracking-[0.3em] text-zinc-600">{section}</p>
              <div className="space-y-1.5">
                {rows.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.key}
                      onClick={() => activate(item)}
                      data-testid={item.testId || `menu-item-${item.key}`}
                      className={`w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors
                        ${item.danger
                          ? "border-red-500/20 bg-red-500/5 hover:bg-red-500/10"
                          : "border-white/10 bg-white/[0.03] hover:bg-white/[0.07]"}`}
                    >
                      <span className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center border
                        ${item.danger
                          ? "border-red-500/30 bg-red-500/10 text-red-400"
                          : "border-[#C5A059]/30 bg-[#C5A059]/10 text-[#C5A059]"}`}>
                        <Icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className={`block text-sm ${item.danger ? "text-red-300" : "text-white"}`}>{item.label}</span>
                        {item.sub && <span className="block text-[11px] text-zinc-500">{item.sub}</span>}
                      </span>
                      <ChevronRight className="w-4 h-4 text-zinc-600 shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};
